import { Currency } from "@redotech/money/iso4217/currencies";
import { getCurrencyFormatter, moneyFormat } from "@redotech/money/util";
import {
  ProductElement,
  ProductLayout,
} from "@redotech/redo-content-builder-common/element-schema";
import ImageIcon from "@redotech/redo-icons/image-plus.svg";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo } from "react";
import { ElementBox } from "./element-box";
import { FormElementProps } from "./index";

const PLACEHOLDER_TITLE = "Product title";
const PLACEHOLDER_PRICE = "0.00";
const PLACEHOLDER_NOTE = "Select a product in the inspector.";

interface ProductSnapshot {
  title?: string;
  imageUrl?: string;
  priceText?: string;
  compareAtPriceText?: string;
}

function getMetaContent(property: string): string | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }
  const propertyMatch = document.querySelector(
    `meta[property="${property}"]`,
  ) as HTMLMetaElement | null;
  if (propertyMatch?.content) {
    return propertyMatch.content;
  }
  const nameMatch = document.querySelector(
    `meta[name="${property}"]`,
  ) as HTMLMetaElement | null;
  return nameMatch?.content;
}

function coerceNumber(value: unknown): number | undefined {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

function getShopifyProductMeta(): any | undefined {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return (globalThis as any).ShopifyAnalytics?.meta?.product;
}

function getShopifyCurrency(): string | undefined {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return (globalThis as any).Shopify?.currency?.active;
}

function getVariantPrice(productMeta: any): number | undefined {
  if (!productMeta) {
    return undefined;
  }
  const variants: any[] = Array.isArray(productMeta.variants)
    ? productMeta.variants
    : [];
  if (variants.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const selectedVariantId = (globalThis as any).ShopifyAnalytics?.meta
      ?.selectedVariantId;
    const selectedVariant =
      variants.find(
        (variant) => String(variant?.id) === String(selectedVariantId),
      ) ?? variants[0];
    const variantPrice = coerceNumber(selectedVariant?.price);
    if (variantPrice !== undefined) {
      return variantPrice;
    }
  }
  return (
    coerceNumber(productMeta.variantPrice) ?? coerceNumber(productMeta.price)
  );
}

function formatShopifyPrice(): string | undefined {
  const amount = getMetaContent("product:price:amount");
  const currency = getMetaContent("product:price:currency");
  if (amount && currency) {
    const parsedAmount = coerceNumber(amount);
    if (parsedAmount !== undefined) {
      try {
        return getCurrencyFormatter(currency).format(parsedAmount);
      } catch {
        return `${parsedAmount} ${currency}`;
      }
    }
  }

  const productMeta = getShopifyProductMeta();
  const rawPrice = getVariantPrice(productMeta);
  if (rawPrice === undefined) {
    return undefined;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const shopifyFormatMoney = (globalThis as any).Shopify?.formatMoney;
  if (typeof shopifyFormatMoney === "function" && Number.isInteger(rawPrice)) {
    try {
      return shopifyFormatMoney(rawPrice);
    } catch {
      // Fall back to formatter below.
    }
  }
  const currencyCode = currency ?? getShopifyCurrency() ?? "USD";
  const normalizedAmount = Number.isInteger(rawPrice)
    ? rawPrice / 100
    : rawPrice;
  try {
    return getCurrencyFormatter(currencyCode).format(normalizedAmount);
  } catch {
    return `${normalizedAmount} ${currencyCode}`;
  }
}

function getProductSnapshot(): ProductSnapshot {
  const productMeta = getShopifyProductMeta();
  const title =
    productMeta?.name ??
    productMeta?.title ??
    getMetaContent("og:title") ??
    getMetaContent("twitter:title");
  const imageUrl =
    productMeta?.featured_image ??
    productMeta?.image ??
    getMetaContent("og:image") ??
    getMetaContent("twitter:image");
  const priceText = formatShopifyPrice();

  return { title, imageUrl, priceText };
}

function getElementProductSnapshot(
  element: ProductElement,
): ProductSnapshot | undefined {
  if (!element.productId) {
    return undefined;
  }
  return {
    title: element.productTitle,
    imageUrl: element.productImageUrl,
    priceText: element.productPrice,
    compareAtPriceText: element.productCompareAtPrice,
  };
}

const ProductImage = memo(function ProductImage({
  src,
  alt,
  layout,
}: {
  src: string;
  alt: string;
  layout: ProductLayout;
}) {
  switch (layout) {
    case ProductLayout.VERTICAL:
      return (
        <Flex borderRadius="lg" overflow="hidden" w="full">
          <img alt={alt} height="auto" src={src} width="100%" />
        </Flex>
      );
    case ProductLayout.HORIZONTAL:
      return (
        <Flex basis="48px" borderRadius="md" overflow="hidden" shrink={0}>
          <img alt={alt} height="48" src={src} width="48" />
        </Flex>
      );
    default:
      return assertNever(layout);
  }
});

const ImagePlaceholder = memo(function ImagePlaceholder({
  layout,
}: {
  layout: ProductLayout;
}) {
  switch (layout) {
    case ProductLayout.VERTICAL:
      return (
        <Flex
          align="center"
          bgColor="tertiary"
          borderRadius="lg"
          justify="center"
          p="5xl"
          textColor="secondary"
          w="full"
        >
          <ImageIcon height={48} width={48} />
        </Flex>
      );
    case ProductLayout.HORIZONTAL:
      return (
        <Flex
          align="center"
          basis="48px"
          bgColor="tertiary"
          borderRadius="md"
          justify="center"
          overflow="hidden"
          p="md"
          shrink={0}
          textColor="secondary"
        >
          <ImageIcon height={24} width={24} />
        </Flex>
      );
    default:
      return assertNever(layout);
  }
});

const PriceDisplay = memo(function PriceDisplay({
  priceText,
  compareAtPriceText,
  fontFamily,
}: {
  priceText: string;
  compareAtPriceText?: string;
  fontFamily?: string;
}) {
  return (
    <Flex align="center" dir="row" gap="xs">
      {compareAtPriceText && (
        <Text
          fontSize="sm"
          style={{ fontFamily: fontFamily ? `'${fontFamily}'` : undefined }}
          textColor="secondary"
          textDecoration="line-through"
        >
          {compareAtPriceText}
        </Text>
      )}
      <Text
        fontSize="sm"
        style={{ fontFamily: fontFamily ? `'${fontFamily}'` : undefined }}
        textColor="primary"
      >
        {priceText}
      </Text>
    </Flex>
  );
});

function getLayoutConfig(layout: ProductLayout) {
  switch (layout) {
    case ProductLayout.VERTICAL:
      return {
        container: { dir: "column" as const },
        titleFontSize: "md" as const,
      };
    case ProductLayout.HORIZONTAL:
      return {
        container: { align: "center" as const, dir: "row" as const },
        titleFontSize: "sm" as const,
      };
    default:
      return assertNever(layout);
  }
}

export const FormProduct = memo(function FormProduct({
  element,
  isBuilder = false,
}: FormElementProps<ProductElement>) {
  const layout = element.productLayout ?? ProductLayout.VERTICAL;
  const configuredProduct = getElementProductSnapshot(element);
  const pageProduct = configuredProduct ? undefined : getProductSnapshot();
  const product = configuredProduct ?? pageProduct ?? {};
  const hasProductData = Boolean(
    product.title || product.imageUrl || product.priceText,
  );

  if (!hasProductData && !isBuilder) {
    return null;
  }

  const displayTitle = product.title ?? PLACEHOLDER_TITLE;
  const titleStyle = {
    fontFamily: element.fontFamily ? `'${element.fontFamily}'` : undefined,
    fontSize: element.fontSize ? `${element.fontSize}px` : undefined,
  };
  const priceAmount =
    product.priceText || (isBuilder ? PLACEHOLDER_PRICE : undefined);
  const displayPrice =
    priceAmount !== undefined
      ? moneyFormat({
          amount: Number(priceAmount),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          currency: getShopifyCurrency() as Currency,
        })
      : undefined;

  const layoutConfig = getLayoutConfig(layout);

  return (
    <ElementBox alignment={element.contentAlignment} bare>
      <Flex {...layoutConfig.container} gap="md" w="full">
        {product.imageUrl ? (
          <ProductImage
            alt={displayTitle}
            layout={layout}
            src={product.imageUrl}
          />
        ) : (
          <ImagePlaceholder layout={layout} />
        )}
        <Flex dir="column" gap="xs">
          <Text
            fontSize={layoutConfig.titleFontSize}
            fontWeight="semibold"
            style={titleStyle}
            textColor="primary"
          >
            {displayTitle}
          </Text>
          {displayPrice && (
            <PriceDisplay
              compareAtPriceText={product.compareAtPriceText}
              fontFamily={element.fontFamily}
              priceText={displayPrice}
            />
          )}
          {isBuilder && !hasProductData && (
            <Text fontSize="xs" textColor="secondary">
              {PLACEHOLDER_NOTE}
            </Text>
          )}
        </Flex>
      </Flex>
    </ElementBox>
  );
});
